import {
	Matrix,
	Quaternion,
	SceneLoader,
	TransformNode,
	Vector3,
	type Scene,
} from "@babylonjs/core";
import {
	ecefToEnuRotation,
	geodeticToEcef,
	type ECEF,
	type UtahRegion,
} from "@utah-vr/shared";

const ROOT = "https://tile.googleapis.com/v1/3dtiles/root.json";
const MAX_DEPTH = 8;
const MAX_TILES = 32;

interface TileJson {
	transform?: number[];
	content?: { uri?: string; url?: string };
	children?: TileJson[];
	boundingVolume?: { box?: number[]; region?: number[]; sphere?: number[] };
	geometricError?: number;
}
interface TilesetJson {
	root?: TileJson;
}

function withKey(url: string, apiKey: string) {
	const u = new URL(url, "https://tile.googleapis.com");
	if (!u.searchParams.has("key")) u.searchParams.set("key", apiKey);
	return u.toString();
}

function compose(parent: Matrix | null, transform: number[] | undefined): Matrix {
	const local = transform && transform.length === 16 ? Matrix.FromArray(transform) : Matrix.Identity();
	return parent ? local.multiply(parent) : local;
}

function worldMatrixForRegion(region: UtahRegion): Matrix {
	const origin = geodeticToEcef(region.center);
	const r = ecefToEnuRotation(region.center);
	const m = Matrix.FromValues(
		r[0]!, r[6]!, r[3]!, 0,
		r[1]!, r[7]!, r[4]!, 0,
		r[2]!, r[8]!, r[5]!, 0,
		0, 0, 0, 1,
	);
	const t = Matrix.Translation(-origin.x, -origin.y, -origin.z);
	return t.multiply(m);
}

function applyMatrix(node: TransformNode, mat: Matrix) {
	const s = new Vector3();
	const q = new Quaternion();
	const tr = new Vector3();
	mat.decompose(s, q, tr);
	node.position = tr;
	node.rotationQuaternion = q;
	node.scaling = s;
}

/**
 * Transform an ECEF point by the tile's accumulated transform (4x4 matrix
 * stored row-major in glTF/3D-Tiles convention) — but since we're testing
 * membership the tile's boundingVolume.box is ALREADY in the post-transform
 * local frame. So we transform the ECEF point INTO that local frame to test it.
 */
function transformEcefToLocal(ecef: ECEF, accumulated: Matrix): { x: number; y: number; z: number } {
	const inv = Matrix.Invert(accumulated);
	const v = Vector3.TransformCoordinates(new Vector3(ecef.x, ecef.y, ecef.z), inv);
	return { x: v.x, y: v.y, z: v.z };
}

/**
 * Test whether a point is inside an OBB defined by 3D Tiles spec:
 * `box = [cx, cy, cz, ux, uy, uz, vx, vy, vz, wx, wy, wz]` where the half-axes
 * already encode magnitude. Point P is inside iff for each half-axis A:
 * `|(P - C) · A| ≤ |A|²`.
 */
function pointInBox(box: number[], p: { x: number; y: number; z: number }): boolean {
	if (box.length < 12) return true; // be permissive on weird shapes
	const cx = box[0]!, cy = box[1]!, cz = box[2]!;
	const dx = p.x - cx, dy = p.y - cy, dz = p.z - cz;
	for (let i = 0; i < 3; i++) {
		const ax = box[3 + i * 3]!;
		const ay = box[4 + i * 3]!;
		const az = box[5 + i * 3]!;
		const dot = dx * ax + dy * ay + dz * az;
		const lenSq = ax * ax + ay * ay + az * az;
		if (dot * dot > lenSq * lenSq) return false;
	}
	return true;
}

let abortedFromAuth = false;
let targetEcef: ECEF | null = null;

export async function attachUtahTiles(scene: Scene, region: UtahRegion): Promise<void> {
	const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY as string | undefined;
	if (!apiKey) {
		console.info(`[tiles] No VITE_GOOGLE_MAPS_API_KEY set — skipping tiles for ${region.displayName}.`);
		return;
	}

	abortedFromAuth = false;
	targetEcef = geodeticToEcef(region.center);
	const tilesRoot = new TransformNode("tiles-root", scene);
	applyMatrix(tilesRoot, worldMatrixForRegion(region));

	const queue: Array<{ url: string; parentMat: Matrix | null; depth: number }> = [
		{ url: withKey(ROOT, apiKey), parentMat: null, depth: 0 },
	];
	let loadedTiles = 0;
	let visited = 0;

	while (queue.length > 0 && loadedTiles < MAX_TILES && !abortedFromAuth) {
		const job = queue.shift()!;
		if (job.depth > MAX_DEPTH) continue;
		let json: TilesetJson | TileJson;
		try {
			const res = await fetch(job.url);
			if (!res.ok) {
				console.warn(`[tiles] ${res.status} ${res.statusText} for ${job.url}`);
				continue;
			}
			json = await res.json();
		} catch (e) {
			console.warn("[tiles] fetch failed", e);
			continue;
		}

		const rootTile: TileJson = "root" in json && json.root ? json.root : (json as TileJson);
		const { loaded, visited: v } = await walkTile(scene, tilesRoot, rootTile, job.parentMat, job.depth, apiKey, queue);
		loadedTiles += loaded;
		visited += v;
	}

	console.info(`[tiles] ${region.displayName}: visited ${visited} tile node(s), loaded ${loadedTiles} mesh tile(s)`);
}

async function walkTile(
	scene: Scene,
	parentNode: TransformNode,
	tile: TileJson,
	parentMat: Matrix | null,
	depth: number,
	apiKey: string,
	queue: Array<{ url: string; parentMat: Matrix | null; depth: number }>,
): Promise<{ loaded: number; visited: number }> {
	if (abortedFromAuth) return { loaded: 0, visited: 0 };
	const here = compose(parentMat, tile.transform);

	// Spatial filter: skip any tile whose boundingVolume.box doesn't contain
	// region.center (in the tile's post-transform local frame).
	if (targetEcef && tile.boundingVolume?.box && tile.boundingVolume.box.length >= 12) {
		const localTarget = transformEcefToLocal(targetEcef, here);
		if (!pointInBox(tile.boundingVolume.box, localTarget)) {
			return { loaded: 0, visited: 0 };
		}
	}

	let loaded = 0;
	let visited = 1;
	const uri = tile.content?.uri ?? tile.content?.url;
	if (uri) {
		const fullUrl = withKey(uri, apiKey);
		const ext = (uri.split("?")[0] ?? "").toLowerCase();
		if (ext.endsWith(".glb") || ext.endsWith(".gltf")) {
			let head: Response | null = null;
			try {
				head = await fetch(fullUrl, { method: "GET", headers: { Range: "bytes=0-0" } });
			} catch {
				/* network — let Babylon try and warn */
			}
			if (head && (head.status === 401 || head.status === 403)) {
				abortedFromAuth = true;
				console.warn(
					`[tiles] aborting: Google returned ${head.status} on tile content. Check Map Tiles API enabled + billing + API key referrer restrictions for this origin.`,
				);
				return { loaded: 0, visited };
			}
			try {
				const result = await SceneLoader.ImportMeshAsync("", "", fullUrl, scene, undefined, ".glb");
				const wrapper = new TransformNode(`tile-${depth}`, scene);
				wrapper.parent = parentNode;
				applyMatrix(wrapper, here);
				for (const m of result.meshes) {
					if (m.parent === null) m.parent = wrapper;
				}
				loaded++;
			} catch (e) {
				console.warn("[tiles] mesh import failed", uri, e);
			}
		} else if (ext.endsWith(".json") || !ext.includes(".")) {
			queue.push({ url: fullUrl, parentMat: here, depth: depth + 1 });
		}
	}

	if (tile.children && depth < MAX_DEPTH && !abortedFromAuth) {
		for (const child of tile.children) {
			const { loaded: l, visited: v } = await walkTile(scene, parentNode, child, here, depth + 1, apiKey, queue);
			loaded += l;
			visited += v;
		}
	}
	return { loaded, visited };
}
